import { Component, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PhoneInputComponent } from '../../ui/phone-input/phone-input';
import { AcceptInvitationResult, PublicInvitation, TeamService } from '../../services/team/team.service';

/** Same rules the API applies to every password */
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/;

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return password && confirm && password !== confirm ? { passwordMismatch: true } : null;
}

/**
 * Public page of a team invitation (app.citalinkpro.com/{business-slug}--{token}).
 * The invitee completes their details and creates a password; no session needed.
 */
@Component({
  selector: 'app-accept-invitation',
  imports: [ReactiveFormsModule, RouterLink, PhoneInputComponent, DatePipe],
  templateUrl: './accept-invitation.html',
  styleUrls: ['../auth-form.css', './accept-invitation.css'],
})
export class AcceptInvitation implements OnInit {
  private teamService = inject(TeamService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  readonly loading = signal(true);
  readonly invitation = signal<PublicInvitation | null>(null);
  readonly notFound = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly accepted = signal<AcceptInvitationResult | null>(null);
  showPassword = false;
  private slug = '';

  form = this.fb.group(
    {
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      phone: [null as { countryId: string; phoneNumber: string } | null, Validators.required],
      password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordsMatch }
  );

  ngOnInit(): void {
    // The slug is matched on the layout route that wraps this page
    this.slug = (this.route.parent ?? this.route).snapshot.paramMap.get('slug') ?? '';
    this.teamService.getPublicInvitation(this.slug).subscribe({
      next: (invitation) => {
        this.invitation.set(invitation);
        this.form.patchValue({
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          phone: invitation.phone && invitation.countryId
            ? { countryId: invitation.countryId, phoneNumber: invitation.phone }
            : null,
        });
        this.loading.set(false);
      },
      error: () => {
        this.notFound.set(true);
        this.loading.set(false);
      },
    });
  }

  showError(name: 'firstName' | 'lastName' | 'phone' | 'password' | 'confirmPassword'): boolean {
    const control = this.form.controls[name];
    return control.invalid && (control.dirty || control.touched);
  }

  get mismatch(): boolean {
    const confirm = this.form.controls.confirmPassword;
    return !!this.form.errors?.['passwordMismatch'] && (confirm.dirty || confirm.touched);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    this.submitting.set(true);
    this.error.set(null);
    this.teamService
      .acceptInvitation(this.slug, {
        firstName: v.firstName!.trim(),
        lastName: v.lastName!.trim(),
        phone: v.phone!.phoneNumber,
        countryId: v.phone!.countryId,
        password: v.password!,
      })
      .subscribe({
        next: (result) => {
          this.submitting.set(false);
          this.accepted.set(result);
        },
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          const errors: string[] | undefined = err.error?.errors;
          this.error.set(errors?.length ? errors.join(' ') : 'No pudimos crear tu cuenta. Intenta de nuevo.');
        },
      });
  }

  goToSignIn(): void {
    this.router.navigate(['/auth/signin'], { queryParams: { email: this.accepted()?.email } });
  }
}
